import type { QuestlogDb } from '../database.ts';
import { registerSchemaActions } from '../surfaces/schema_actions.ts';
import { minimalInput } from '../surfaces/schema_examples.ts';
import { validateInput } from '../surfaces/validate_input.ts';
import { actionRequirements } from './action_requirements.ts';
import { type ToolResult, toolFailure } from './tool_types.ts';

export type ToolInputDescriptions = Readonly<Record<string, string>>;

export type ToolOutputDescription = string;

export type JsonSchemaType =
	| 'array'
	| 'boolean'
	| 'integer'
	| 'null'
	| 'number'
	| 'object'
	| 'string';

export interface JsonSchema {
	type?: JsonSchemaType | undefined;
	description?: string | undefined;
	enum?: ReadonlyArray<boolean | number | string | null> | undefined;
	const?: boolean | number | string | null;
	items?: JsonSchema | undefined;
	properties?: Readonly<Record<string, JsonSchema>> | undefined;
	required?: ReadonlyArray<string> | undefined;
	additionalProperties?: boolean | undefined;
	oneOf?: ReadonlyArray<JsonSchema> | undefined;
	anyOf?: ReadonlyArray<JsonSchema> | undefined;
}

export type ToolSideEffects = 'none' | 'writes_state';

export interface QuestlogToolDefinition<
	TInput,
	TResult extends ToolResult<unknown> | Promise<ToolResult<unknown>>,
> {
	name: string;
	description: string;
	whenToUse: string;
	whenNotToUse?: string | undefined;
	sideEffects: ToolSideEffects;
	readOnly: boolean;
	supportsClarification: boolean;
	targetKinds: readonly string[];
	inputDescriptions: ToolInputDescriptions;
	outputDescription: ToolOutputDescription;
	inputSchema: JsonSchema;
	handler: (db: QuestlogDb, input: TInput) => TResult;
}

export type ToolDefinitionRegistry = readonly QuestlogToolDefinition<
	never,
	ToolResult<unknown> | Promise<ToolResult<unknown>>
>[];

export function defineQuestlogTool<
	TInput,
	TResult extends ToolResult<unknown> | Promise<ToolResult<unknown>>,
>(definition: QuestlogToolDefinition<TInput, TResult>): QuestlogToolDefinition<TInput, TResult> {
	const requirements = actionRequirements[definition.name];
	if (requirements) registerSchemaActions(definition.inputSchema, requirements);
	const handler = definition.handler;
	definition.handler = (db, input) => {
		const errors = validateInput(definition.inputSchema, input);
		if (errors.length) {
			const action = (input as Record<string, unknown> | null)?.action;
			const example = minimalInput(definition.inputSchema, action);
			const message = errors.join('; ');
			return toolFailure('protocol', 'invalid_input', message, message, {
				recovery: `${message}; pass ${JSON.stringify(example)}`,
			}) as TResult;
		}
		return handler(db, input);
	};
	return definition;
}

export function stringSchema(description: string, options: Partial<JsonSchema> = {}): JsonSchema {
	return { type: 'string', description, ...options };
}

export function numberSchema(description: string, options: Partial<JsonSchema> = {}): JsonSchema {
	return { type: 'number', description, ...options };
}

export function integerSchema(description: string, options: Partial<JsonSchema> = {}): JsonSchema {
	return { type: 'integer', description, ...options };
}

export function booleanSchema(description: string, options: Partial<JsonSchema> = {}): JsonSchema {
	return { type: 'boolean', description, ...options };
}

export function enumSchema(
	description: string,
	values: ReadonlyArray<boolean | number | string | null>,
): JsonSchema {
	const schema: JsonSchema = {
		description,
		enum: values,
	};
	if (values.every((value) => typeof value === 'string')) {
		schema.type = 'string';
	}
	return schema;
}

export function literalSchema(
	value: boolean | number | string | null,
	description?: string,
): JsonSchema {
	const schema: JsonSchema = {
		type:
			value === null
				? 'null'
				: typeof value === 'string'
					? 'string'
					: typeof value === 'number'
						? 'number'
						: 'boolean',
		const: value,
	};
	if (description !== undefined) {
		schema.description = description;
	}
	return schema;
}

export function arraySchema(items: JsonSchema, description: string): JsonSchema {
	return {
		type: 'array',
		description,
		items,
	};
}

export function objectSchema(
	properties: Record<string, JsonSchema>,
	required: ReadonlyArray<string>,
	description?: string,
): JsonSchema {
	const schema: JsonSchema = {
		type: 'object',
		properties,
		required,
		additionalProperties: false,
	};
	if (description !== undefined) {
		schema.description = description;
	}
	return schema;
}

export function oneOfSchema(schemas: ReadonlyArray<JsonSchema>, description?: string): JsonSchema {
	const schema: JsonSchema = {
		oneOf: schemas,
	};
	if (description !== undefined) {
		schema.description = description;
	}
	return schema;
}
